<template>
  <div class="pp-interaction-title pb-2">
    <p aria-live="polite" class="aria-feedback">{{interactionFeedbackAria}}</p>
    <div
      :key="fitbKey"
      ref="fillHeader"
      :class="classObj"
      role="heading"
      aria-level="2"
      v-html="interaction.interactionText">
    </div>
    <div
      class="feedback-container pt-2"
      v-if="showFeedback">
      <div
        v-for="(io, idx) in interaction.interactionOptions"
        :class="!interactionMeta.darkMode ? feedbackType(io) : 'dark-mode-' + feedbackType(io)"
        class="short-answer-feedback clearfix"
        :key="idx">
        <blockquote
          :key="interactionShortAnswerFeedbackKey"
          class="feedback-blockquote clearfix position-fix short-answer">
            <p
              v-if="!neverShowHighlighting"
              :class="{ 'mb-0': !io.feedback, 'white--text': interactionMeta.darkMode }">
              Correct answer {{idx + 1}} -
                <v-chip
                v-for="(i, index) in interactionMeta.correctAnswers[idx].option.split(delimiter)"
                :key="index"
                color='grey lighten-1'
                class="pointer"
                :aria-label="`${interactionMeta.correctAnswers[idx].option.split(delimiter)[index]}`"
              >
                <span class="grey--text text--darken-3 text-xs-center">
                  {{interactionMeta.correctAnswers[idx].option.split(delimiter)[index]}}
                </span>
              </v-chip>
            </p>
          <span v-if="!neverShowHighlighting">
            {{ (interactionMeta.individualAnswerStatuses[io.id]) ? 'Correct' : 'Incorrect' }}
          </span>
          <span
            v-show="io.feedback"
            v-html="io.feedback"
            :class="{ 'white--text': interactionMeta.darkMode }">
          </span>
          <interaction-attachments :attachments="io.interactionOptionFeedbackAttachments" />
        </blockquote>
      </div>
    </div>
  </div>
</template>
<script>
/* global _ */
/* global dataLayer */

// Components:
import InteractionAttachments from '@/components/interaction/InteractionAttachments';
import EventBus from '@/buses/EventBus';

// Libraries / Plugins:
import {
  mapState,
  mapMutations,
  mapGetters
} from 'vuex';

export default {
  name: 'interaction-short-answer',
  components: {
    InteractionAttachments,
  },
  props: [
    'interactionId',
    'frame_id',
    'visible',
  ],
  data() {
    return {
      correctAnswerData: [],
      fitbKey: 0,
      inputBlanks: [],
      initial: false,
      interactionFeedbackAria: '',
      interactionShortAnswerFeedbackKey: 1,
    };
  },
  computed: {
    ...mapGetters('player', [
      'mobile',
      'hasNewInteractionDisplayAccess',
    ]),
    delimiter() {
      return this?.interaction?.config?.hasNewFillInTheBlankDelimiter
        ? '^||*|||*||^'
        : ',';
    },
    aria() {
      return {id: `interaction-text-${this.interaction.id}`}
    },
    classObj() {
      let obj = { 'pp-interaction-body': !this.interactionMeta.rubric, 'darkTheme': this.interactionMeta.darkMode, 'pastSessionTime': this.interactionMeta.pastSessionTime };
      if (this.interactionMeta.rubric) obj[`out-of-${this.interaction.pointsAvailable}`] = true;
      return obj;
    },
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    showFeedback() {
      return (
        this.interactionMeta.isInteractionAnswered
        && (
          (
            this.interaction.attempts > 0
            && this.interactionMeta.attempts >= this.interaction.attempts
          )
          || this.interactionMeta.isAnswerCorrect
          || (
            _.has(this.interaction, 'config.show_feedback_last_attempt_only')
            && !this.interaction.config.show_feedback_last_attempt_only
          )
        ) && !this.interactionMeta.rubric
      );
    },
    inputBuffer() {
      if (this.interaction.config.response_is_score && this.interaction.config.numeric_only) {
        return 10;
      }
      return 0;
    },
    neverShowHighlighting() {
      return _.get(this.interaction, 'config.hide_correct_answer_always', 0) === 1;
    }
  },
  methods: {
    ...mapMutations('interactions', [
      'setInteractionStates',
    ]),
    constructAriaFeedback() {
      let ariaFeedback = '';
      for (const io of this.interaction.interactionOptions) {
        const selectionFeedback = `Answer ${io.option.replace(/(\<.*?\>)/g, '')} was ${this.formatFeedback(this.feedbackType(io))}. ${this.showFeedback ? io.feedback.replace(/(\<.*?\>)/g, '') : ''} `;
        ariaFeedback += selectionFeedback;
      }
      setTimeout(() => {this.interactionFeedbackAria = ariaFeedback;}, 100); // JAWS will not read aria-live region without this timeout
    },
    formatFeedback(text) {
      return _.startCase(_.toLower(text));
    },
    updateShortAnswer(idx, text) {
      // console.log('updateShortAnswer()', idx, text);
      this.$set(this.interactionAttempt.responseText, idx, text.trim());
      // start assuming the blanks are answered, if any blank has a length of 0 it will make the isAnswerEmpty false
      this.interactionMeta.isAnswerEmpty = !_.reduce(this.interactionAttempt.responseText, (blank, el) => blank && el.length > 0, true)
    },
    handleCorrectAnswers(correctAnswers) {
      let answers = this.interaction.interactionOptions.map(io => io.option);
      this.interaction.interactionOptions.forEach((io, idx) => {
        if (this.showFeedback && !this.neverShowHighlighting) {
          if (this.interactionMeta.individualAnswerStatuses[io.id]) {
            this.inputBlanks[idx].style['border-color'] = '#4CAF50';
          } else {
            this.inputBlanks[idx].style['border-color'] = '#F44336';
          }
        }
      });
    },
    setInputBlankWidth(ib) {
      // ib is an input blank DOM element
      // ib.style.width = (ib.value.length > 0 && ib.value.length < 14) ? ((ib.value.length)*10 + this.inputBuffer) + 'px' : ((ib.placeholder.length)*10) + 'px';
      // Gets the current input width and converts it to a number
      const inputLength = Number(ib.style.width.slice(0, ib.style.width.indexOf('px')));
      // Extends the length og the input if it is larger than 17 characters, but not longer than the sidebar panel
      if(ib.value.length > 17 && inputLength < 380) ib.style.width = (ib.value.length *8.5) + 'px';
      // Resets length back to 150 if the answer is deleted
      if(ib.value.length === 0) ib.style.width = '150px';
    },
    constructInputBlanks() {
      this.inputBlanks = this.$refs.fillHeader.querySelectorAll('input');
      if (!this.interactionAttempt.interactionOptionIds.length) this.interactionAttempt.interactionOptionIds = _.map(this.interaction.interactionOptions,'id');
      this.inputBlanks.forEach((ib, i) => {
        ib.readOnly = false;
        ib.removeAttribute('data-answer');
        this.setInputBlankWidth(ib);
        // check vuex first for values and then updateShortAnswer
        var answer = this.interactionAttempt.responseText[i];
        if (!answer) {
          this.interactionAttempt.responseText[i] = '';
          answer = this.interactionAttempt.responseText[i];
        }
        this.updateShortAnswer(i, answer);
        ib.value = answer;
        ib.setAttribute('aria-label', `input blank ${i + 1}`);
        ib.setAttribute('tabindex', 0);
        ib.addEventListener('keypress', (event) => {
          if (event.charCode === 13) event.preventDefault();
        });
        if (this.interactionMeta.rubric && this.mobile) ib.pattern="\\d*"; // add pattern to limit iphone keyboard
        ib.addEventListener('input', (event) => {
          event.preventDefault();
          if (this.interactionMeta.rubric) {
            // limit input answer to 0-5
            const intVal = parseInt(ib.value);
            if (_.isNaN(intVal)) ib.value = '';
            if (intVal > this.interaction.pointsAvailable) ib.value = this.interaction.pointsAvailable;
            if (intVal < 0) ib.value = '0';
          }
          this.setInputBlankWidth(ib);
          this.updateShortAnswer(i, ib.value);
          if (this.hasNewInteractionDisplayAccess) this.setInteractionStates();
        })
      });
    },
    resetInputBlanks() {
      if (!this.interactionAttempt.interactionOptionIds.length) this.interactionAttempt.interactionOptionIds = _.map(this.interaction.interactionOptions,'id');
      this.inputBlanks.forEach((ib, i) => {
        ib.value = '';
        ib.style['border-color'] = '#C9C9C9';
        this.setInputBlankWidth(ib);
        var answer = this.interactionAttempt.responseText[i];
        if (!answer) {
          this.interactionAttempt.responseText[i] = '';
          answer = this.interactionAttempt.responseText[i];
        }
        this.updateShortAnswer(i, answer);
      });
    },
    disableInputBlanks() {
      this.inputBlanks.forEach((ib) => ib.disabled = true);
    },
    enableInputBlanks() {
      this.inputBlanks.forEach((ib) => ib.disabled = false);
    },
    feedbackType(io) {
      // if hide_correct_answer_always - dont show correct/incorrect
      if (_.get(this, 'interaction.config.hide_correct_answer_always', false) || (this.interactionMeta.attempts < this.interaction.attempts && _.get(this.interaction, 'config.show_highlighting_last_attempt_only', false) && !this.interactionMeta.isAnswerCorrect)) return '';
      return this.interactionMeta.individualAnswerStatuses[io.id] ? 'correct' : 'incorrect';
    },
  },
  watch: {
    'visible'(newValue, oldValue) {
      if (this.visible) {
        this.constructInputBlanks();
        if (this.interactionMeta.isInteractionAnswered) {
          this.disableInputBlanks();
          this.handleCorrectAnswers();
        }
      }
      if (
        this.visible
        && (
          this.interaction.interactionText.includes('h5p.com')
        )
      ) {
        this.fitbKey += 1;
        this.$nextTick(() => this.constructInputBlanks());
      }
      if (!newValue && oldValue) this.interactionShortAnswerFeedbackKey += 1;
    },
    'interactionMeta.isInteractionAnswered'(newValue) {
      if (newValue) this.constructAriaFeedback();
      if (this.interactionMeta.isRetake && !this.interactionMeta.isInteractionAnswered) {
        this.resetInputBlanks();
        this.enableInputBlanks();
      }
      else if (this.interactionMeta.isInteractionAnswered) {
        this.disableInputBlanks();
      }
    }
  },
  mounted() {
    EventBus.$on('beganSubmittingAnswer', (interactionId) => {
      if (interactionId === this.interactionId) {
        this.disableInputBlanks();
      }
    });
    EventBus.$on('finishedSubmittingAnswer', (interactionAttempt) => {
      if (interactionAttempt.interactionId === this.interactionId) {
        this.handleCorrectAnswers();
      }
    });
    EventBus.$on('retakeInteraction', (interactionId) => {
      if (this.interaction.id === interactionId) {
        this.enableInputBlanks();
        this.resetInputBlanks();
        this.interactionShortAnswerFeedbackKey += 1;
      }
    });
  }
};
</script>

<style>
  .q-header input[type=text],
  .q-header input[type=text]:not(.browser-default) {
    width: 150px;
    text-align: left;
  }

  .pp-interaction-title input {
    box-sizing: border-box;
    max-width: 100%;
  }
  .darkTheme {
    color: white;
  }
  .pastSessionTime {
    pointer-events: none;
  }

  .feedback-container {
    /* to prevent floated tables in the interaction from crush feedback blockquotes */
    clear: both;
  }
</style>
